import React from "react";
import Fab from "../../component/FAB";
import Nav from "../../component/Nav";
import Modal from "../../component/Modal";
import {
  IoMdAdd,
  IoIosWallet,
  IoIosArrowForward,
  IoIosCalendar,
  IoIosClose,
  IoIosSearch,
  IoIosApps,
  IoIosList,
} from "react-icons/io";
import {
  FormatDate,
  useLocalStorage,
  expiredStorage,
} from "../../helpers";
import { cashflowGet, cashflowDelete } from "../../api";
import Empty from "../../component/Empty";
import ButtonLoading from "../../component/ButtonLoading";
import TransactionLoader from "../../loader/TransactionLoader";
import TransactionCard from "./TransactionCard";
import TransactionRow from "./TransactionRow";
import { CHALLANGE_TEXT } from "../../constant";
import "./filterBar.css";
import "./transaction-views.css";

const SCROLL_KEY = "trx_scrollY";

const toStartIso = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T00:00:00.000Z` : null;
const toFinishIso = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T23:59:59.999Z` : null;

const formatRangeLabel = (start, finish) => {
  if (!start && !finish) return "Filter Tanggal";
  if (start && finish) return `${FormatDate(start)} – ${FormatDate(finish)}`;
  if (start) return `Sejak ${FormatDate(start)}`;
  return `Hingga ${FormatDate(finish)}`;
};

export default (props) => {
  const PAGE_SIZE = 20;
  const walletTrx = JSON.parse(localStorage.getItem("walletTrx"));
  let walletId = walletTrx === null ? "0" : walletTrx.id;
  const [data, setData] = useLocalStorage("trx_data", []);
  const [lastPage, setLastPage] = useLocalStorage("trx_lastPage", false);
  const [currentWalletId, setCurrentWalletId] = useLocalStorage(
    "trx_walletId",
    "0"
  );
  const [dateRange, setDateRange] = useLocalStorage("trx_dateRange", {
    start: null,
    finish: null,
  });
  const [keyword, setKeyword] = useLocalStorage("trx_keyword", "");
  const [searchDraft, setSearchDraft] = React.useState(keyword || "");
  const [viewMode, setViewMode] = useLocalStorage("trx_viewMode", "card");

  const [modal, setModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadMore, setLoadMore] = React.useState(false);
  const [deletedTrx, setDeletedTrx] = React.useState(null);
  const [inputChallange, setInputChallange] = React.useState("");
  const [errorChallange, setErrorChallange] = React.useState("");

  const [dateModal, setDateModal] = React.useState(false);
  const [draftStart, setDraftStart] = React.useState(dateRange.start || "");
  const [draftFinish, setDraftFinish] = React.useState(dateRange.finish || "");
  const [dateError, setDateError] = React.useState("");
  const [focusedId, setFocusedId] = React.useState(null);

  const hasDateFilter = Boolean(dateRange.start || dateRange.finish);

  const fetchInitial = async (wid, range, kw) => {
    try {
      setLoading(true);
      const response = await cashflowGet(
        null,
        PAGE_SIZE,
        wid,
        null,
        toStartIso(range.start),
        toFinishIso(range.finish),
        kw
      );
      setData(response.data.content);
      setLastPage(response.data.last);
      setCurrentWalletId(wid || "0");
    } catch (err) {}
    setLoading(false);
  };

  React.useEffect(() => {
    const isExpired = expiredStorage("trx_data");
    const isWalletChange = currentWalletId !== walletId;
    const willFetchFresh = isExpired || isWalletChange || data.length === 0;
    if (willFetchFresh) {
      localStorage.removeItem(SCROLL_KEY);
      fetchInitial(walletId, dateRange, keyword);
      return;
    }
    const focusId = localStorage.getItem("trx_focusId");
    if (focusId) {
      localStorage.removeItem("trx_focusId");
      requestAnimationFrame(() => {
        const el = document.getElementById("trx-card-" + focusId);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "center" });
          setFocusedId(focusId);
          setTimeout(() => setFocusedId(null), 1600);
        }
      });
      return;
    }
    const saved = parseInt(localStorage.getItem(SCROLL_KEY) || "0", 10);
    if (saved > 0) {
      requestAnimationFrame(() => window.scrollTo(0, saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let raf = null;
    const handler = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        localStorage.setItem(SCROLL_KEY, String(window.scrollY));
        raf = null;
      });
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      window.removeEventListener("scroll", handler);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  // Debounce the search input into the persisted `keyword` state
  React.useEffect(() => {
    if (searchDraft === keyword) return;
    const t = setTimeout(() => setKeyword(searchDraft), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // When the (debounced) keyword actually changes, reset paging & refetch
  const skipKeywordEffect = React.useRef(true);
  React.useEffect(() => {
    if (skipKeywordEffect.current) {
      skipKeywordEffect.current = false;
      return;
    }
    window.scrollTo(0, 0);
    localStorage.removeItem(SCROLL_KEY);
    fetchInitial(walletId, dateRange, keyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const remove = () => {
    if (inputChallange !== CHALLANGE_TEXT) {
      setErrorChallange("Challange Text tidak sesuai");
      return;
    }
    cashflowDelete(deletedTrx.id);
    setModal(false);
    setData(data.filter((d) => d.id !== deletedTrx.id));
  };
  const closeModal = () => {
    setErrorChallange("");
    setInputChallange("");
    setModal(false);
  };
  const fetchMore = async (e) => {
    e.preventDefault();
    try {
      setLoadMore(true);
      const o = data[data.length - 1];
      const response = await cashflowGet(
        o.date,
        PAGE_SIZE,
        walletId,
        null,
        toStartIso(dateRange.start),
        toFinishIso(dateRange.finish),
        keyword
      );
      setData([...data, ...response.data.content]);
      setLastPage(response.data.last);
      setLoadMore(false);
    } catch (err) {
      setLoadMore(false);
    }
  };

  const openDateFilter = () => {
    setDraftStart(dateRange.start || "");
    setDraftFinish(dateRange.finish || "");
    setDateError("");
    setDateModal(true);
  };

  const applyDateFilter = () => {
    if (draftStart && draftFinish && draftStart > draftFinish) {
      setDateError("Tanggal 'Dari' harus lebih awal dari 'Sampai'.");
      return;
    }
    const next = {
      start: draftStart || null,
      finish: draftFinish || null,
    };
    setDateRange(next);
    setDateModal(false);
    window.scrollTo(0, 0);
    localStorage.removeItem(SCROLL_KEY);
    fetchInitial(walletId, next, keyword);
  };

  const resetDateFilter = () => {
    setDraftStart("");
    setDraftFinish("");
    setDateError("");
    const next = { start: null, finish: null };
    setDateRange(next);
    setDateModal(false);
    window.scrollTo(0, 0);
    localStorage.removeItem(SCROLL_KEY);
    fetchInitial(walletId, next, keyword);
  };

  const clearDateFilterFromChip = (e) => {
    e.stopPropagation();
    const next = { start: null, finish: null };
    setDateRange(next);
    setDraftStart("");
    setDraftFinish("");
    window.scrollTo(0, 0);
    localStorage.removeItem(SCROLL_KEY);
    fetchInitial(walletId, next, keyword);
  };

  const selectWallet = () => {
    walletId = walletId || "0";
    props.history.push(`/wallet/search/${walletId}/walletTrx/all`);
  };

  const clearSearch = () => {
    setSearchDraft("");
    setKeyword("");
  };

  const hasWalletFilter = walletTrx !== null;
  const dateChipLabel = hasDateFilter
    ? formatRangeLabel(dateRange.start, dateRange.finish)
    : "Tanggal";

  const filterRow = (
    <div className="txn-filter">
      <div className="txn-search">
        <span className="txn-search__icon">
          <IoIosSearch size="1.2em" />
        </span>
        <input
          type="search"
          className="txn-search__input"
          placeholder="Cari deskripsi transaksi…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          aria-label="Cari deskripsi transaksi"
        />
        {searchDraft && (
          <button
            type="button"
            className="txn-search__clear"
            onClick={clearSearch}
            aria-label="Hapus pencarian"
          >
            <IoIosClose size="1.25em" />
          </button>
        )}
      </div>
      <div className="txn-filter__divider" />
      <div className="txn-chips">
        <button
          type="button"
          className={`txn-chip ${hasWalletFilter ? "txn-chip--active" : ""}`}
          onClick={selectWallet}
          aria-label="Pilih wallet"
        >
          <span className="txn-chip__icon">
            <IoIosWallet size="1em" />
          </span>
          <span className="txn-chip__label">
            {hasWalletFilter ? walletTrx.name : "Semua wallet"}
          </span>
          <span className="txn-chip__caret">
            <IoIosArrowForward size="0.85em" />
          </span>
        </button>
        <button
          type="button"
          className={`txn-chip ${hasDateFilter ? "txn-chip--active" : ""}`}
          onClick={openDateFilter}
          aria-label="Filter tanggal"
        >
          <span className="txn-chip__icon">
            <IoIosCalendar size="1em" />
          </span>
          <span className="txn-chip__label">{dateChipLabel}</span>
          {hasDateFilter ? (
            <span
              className="txn-chip__dismiss"
              onClick={clearDateFilterFromChip}
              aria-label="Reset tanggal"
              role="button"
            >
              <IoIosClose size="1em" />
            </span>
          ) : (
            <span className="txn-chip__caret">
              <IoIosArrowForward size="0.85em" />
            </span>
          )}
        </button>
      </div>
    </div>
  );

  const dateModalContent = (
    <Modal
      show={dateModal}
      title="Filter Tanggal"
      onClose={() => setDateModal(false)}
      onPositive={applyDateFilter}
      onNegative={resetDateFilter}
      textPositive="Terapkan"
      textNegative="Reset"
    >
      <div className="form-group">
        <label className="d-block mb-1">
          <small className="text-muted">Dari</small>
        </label>
        <input
          type="date"
          className="form-control"
          value={draftStart}
          max={draftFinish || undefined}
          onChange={(e) => setDraftStart(e.target.value)}
        />
      </div>
      <div className="form-group mb-0">
        <label className="d-block mb-1">
          <small className="text-muted">Sampai</small>
        </label>
        <input
          type="date"
          className="form-control"
          value={draftFinish}
          min={draftStart || undefined}
          onChange={(e) => setDraftFinish(e.target.value)}
        />
      </div>
      {dateError.length > 0 && (
        <small className="text-danger d-block mt-2">{dateError}</small>
      )}
    </Modal>
  );

  if (loading)
    return (
      <>
        <Nav title="Catatan" />
        <div className="main-padding">
          {filterRow}
          <TransactionLoader />
        </div>
        {dateModalContent}
      </>
    );

  if (data.length === 0) {
    return (
      <>
        <Nav title="Catatan" />
        <div className="main-padding">
          {filterRow}
          <Empty />
        </div>
        <Fab
          icon={IoMdAdd}
          onClick={(e) => {
            localStorage.removeItem("walletSearch");
            props.history.push("/transaction/add");
          }}
        />
        {dateModalContent}
      </>
    );
  }

  const renderToolbar = (
    <div className="trx-toolbar">
      <div className="trx-toolbar__count">
        <strong>{data.length}</strong> transaksi
        {!lastPage && "+"}
      </div>
      <div
        className="trx-viewtoggle"
        role="tablist"
        aria-label="Mode tampilan"
      >
        <button
          type="button"
          className={`trx-viewtoggle__btn ${
            viewMode === "card" ? "trx-viewtoggle__btn--active" : ""
          }`}
          aria-label="Tampilan kartu"
          aria-pressed={viewMode === "card"}
          onClick={() => setViewMode("card")}
        >
          <IoIosApps />
        </button>
        <button
          type="button"
          className={`trx-viewtoggle__btn ${
            viewMode === "table" ? "trx-viewtoggle__btn--active" : ""
          }`}
          aria-label="Tampilan tabel"
          aria-pressed={viewMode === "table"}
          onClick={() => setViewMode("table")}
        >
          <IoIosList />
        </button>
      </div>
    </div>
  );

  const onDelete = (trx) => {
    setModal(true);
    setDeletedTrx(trx);
  };

  return (
    <>
      <Nav title="Catatan" />
      <div className="main-padding">
        {filterRow}
        {renderToolbar}
        {viewMode === "card" ? (
          data.map((d, i) => (
            <TransactionCard
              data={d}
              key={d.id || i}
              isFocused={d.id === focusedId}
              {...props}
              onDelete={onDelete}
            />
          ))
        ) : (
          <div className="trx-table">
            {data.map((d, i) => (
              <TransactionRow
                data={d}
                key={d.id || i}
                isFocused={d.id === focusedId}
                {...props}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
        {loadMore ? (
          <ButtonLoading />
        ) : (
          !lastPage && (
            <button
              className="btn btn-outline-primary btn-block my-4"
              onClick={fetchMore}
            >
              Load More
            </button>
          )
        )}
        <Fab
          icon={IoMdAdd}
          onClick={(e) => {
            localStorage.removeItem("walletSearch");
            props.history.push("/transaction/add");
          }}
        />
        <Modal
          show={modal}
          title="Delete Transaction"
          onClose={() => setModal(false)}
          onPositive={remove}
          onNegative={closeModal}
        >
          Proses ini tidak dapat di-undo. Jika Anda yakin, silakan tulis&nbsp;
          <strong className="text-danger">{CHALLANGE_TEXT}</strong> di bawah
          ini.
          <input
            type="text"
            className="form-control mt-1"
            value={inputChallange}
            onChange={(e) => setInputChallange(e.target.value)}
          />
          {errorChallange.length > 0 && (
            <small className="text-danger">{errorChallange}</small>
          )}
        </Modal>
        {dateModalContent}
      </div>
    </>
  );
};
